import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SHARE_PERMISSIONS,
  type DirectoryEntry,
  type ShareEntry,
  type SharePermission,
  type ShareSubjectType,
} from '@system-atlas/shared';
import { ApiError, api } from '../lib/api';
import { Alert, Button, Label, Modal, Select, TextInput } from './ui';

export interface ShareSubject {
  type: ShareSubjectType;
  id: string;
  name: string;
}

interface ShareDialogProps {
  subject: ShareSubject | null;
  onClose: () => void;
}

export function ShareDialog({ subject, onClose }: ShareDialogProps) {
  const { t } = useTranslation();
  const [shares, setShares] = useState<ShareEntry[]>([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DirectoryEntry[]>([]);
  const [selected, setSelected] = useState<DirectoryEntry | null>(null);
  const [permission, setPermission] = useState<SharePermission>('view');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const load = async (s: ShareSubject) => {
    try {
      const { shares: rows } = await api.listShares(s.type, s.id);
      setShares(rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  useEffect(() => {
    if (subject) {
      setError(null);
      setQuery('');
      setSelected(null);
      setResults([]);
      void load(subject);
    }
  }, [subject?.type, subject?.id]);

  // Typeahead: Suche entkoppelt vom Tippen (einfacher Debounce).
  useEffect(() => {
    if (selected) return;
    const term = query.trim();
    const handle = setTimeout(async () => {
      try {
        const { entries } = await api.searchDirectory(term, 'all');
        setResults(entries);
      } catch {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, selected]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!subject || !selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.addShare({
        subjectType: subject.type,
        subjectId: subject.id,
        granteeType: selected.type,
        granteeId: selected.id,
        permission,
      });
      setSelected(null);
      setQuery('');
      await load(subject);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (shareId: string) => {
    if (!subject) return;
    setError(null);
    try {
      await api.removeShare(shareId);
      await load(subject);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  return (
    <Modal open={!!subject} title={t('share.title', { name: subject?.name ?? '' })} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}

        <form onSubmit={add} className="space-y-2">
          <div>
            <Label htmlFor="share-search">{t('share.searchLabel')}</Label>
            {selected ? (
              <div className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
                <span>
                  {selected.label} <span className="text-slate-400">({selected.sublabel})</span>
                </span>
                <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => setSelected(null)} aria-label={t('common.cancel')}>
                  ×
                </button>
              </div>
            ) : (
              <TextInput
                id="share-search"
                value={query}
                placeholder={t('share.searchPlaceholder')}
                onChange={(e) => setQuery(e.target.value)}
              />
            )}
          </div>

          {!selected && results.length > 0 && (
            <ul className="max-h-44 overflow-auto rounded border border-slate-200 text-sm dark:border-slate-700">
              {results.map((r) => (
                <li key={`${r.type}:${r.id}`}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                    onClick={() => { setSelected(r); setResults([]); }}
                  >
                    <span>{r.label} <span className="text-slate-400">({r.sublabel})</span></span>
                    <span className="text-xs text-slate-400">{t(`share.granteeType.${r.type}`)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-end gap-2">
            <Select value={permission} onChange={(e) => setPermission(e.target.value as SharePermission)}>
              {SHARE_PERMISSIONS.map((p) => (
                <option key={p} value={p}>{t(`share.permission.${p}`)}</option>
              ))}
            </Select>
            <Button type="submit" disabled={busy || !selected}>
              {busy ? t('common.pleaseWait') : t('share.add')}
            </Button>
          </div>
        </form>

        <div className="divide-y divide-slate-100 dark:divide-slate-700">
          {shares.length === 0 ? (
            <p className="py-3 text-sm text-slate-500">{t('share.none')}</p>
          ) : (
            shares.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-2 text-sm">
                <span>
                  {s.label} <span className="text-slate-400">({s.sublabel})</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{t(`share.permission.${s.permission}`)}</span>
                  {confirmingId === s.id ? (
                    <span className="flex items-center gap-1">
                      <Button variant="danger" className="px-2 py-0.5 text-xs" onClick={() => { setConfirmingId(null); void remove(s.id); }}>
                        {t('share.removeAction')}
                      </Button>
                      <Button variant="secondary" className="px-2 py-0.5 text-xs" onClick={() => setConfirmingId(null)}>
                        {t('common.cancel')}
                      </Button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      aria-label={t('share.remove', { name: s.label })}
                      title={t('share.remove', { name: s.label })}
                      className="rounded px-1 text-slate-400 transition-colors hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 dark:hover:text-red-400"
                      onClick={() => setConfirmingId(s.id)}
                    >
                      ×
                    </button>
                  )}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
